import type { ApiData } from '@app/api/typed';
import { t } from '@app/i18n';

/**
 * Postura do Site (P5) — o modelo da tela.
 *
 * Arquivo próprio, e não entradas em `TenantSettingsModel`, porque o Site **não é**
 * o tenant: ele já é outra entidade no banco (B2) e virou coleção com tela própria
 * na P1 (`docs/artefatos/multisite-telas.html` › «Sites», grupo Controle).
 *
 * **Mudou-se para cá em 09/09**, como a versão anterior deste comentário previa:
 * «quando aquele drawer nascer, este arquivo se move inteiro para lá». O drawer
 * nasceu (`Sites/SiteDrawer.tsx`) e o dono do vocabulário é o Site, não o tenant.
 *
 * **S-c:** o card de T21 saiu, e com ele o último leitor de `GET /api/v1/site` e de
 * `PUT /api/v1/site/posture` — as rotas de um site só. O tipo passou a sair da
 * COLEÇÃO: derivá-lo das rotas antigas deixaria este arquivo a quebrar no marco da
 * limpeza, por um motivo que nada tem a ver com postura.
 *
 * **E o marco veio: as duas rotas foram apagadas em 11/09.** A previsão estava certa e
 * a precaução pagou-se — este arquivo não mudou uma linha na remoção. O que ficou por
 * mais tempo do que devia foi a própria remoção: o comentário dizia «que o S-e apaga»,
 * e o S-e não a fez; a rota ficou viva e sem chamador por dois dias. Previsão escrita
 * não é tarefa agendada.
 */

export type SitePosture = ApiData<'/api/v1/sites'>['items'][number]['posture'];

/**
 * A ordem dos níveis vem do SERVIDOR (`site.postures`), nunca de uma constante
 * daqui: o vocabulário é regra de domínio, e transcrevê-lo criaria um segundo
 * lugar para a mesma lista — os dois discordariam no dia de um quarto nível, sem
 * nada ficar vermelho. Este fallback existe só para a tela desenhar enquanto a
 * primeira leitura não chegou.
 */
export const POSTURES_FALLBACK: SitePosture[] = [
  'authorization',
  'authority_governance',
  'artifact_binding',
];

/** O nível numérico, que é o que diz se a troca é uma REDUÇÃO de garantia. */
export function postureLevel(posture: SitePosture): number {
  switch (posture) {
    case 'authorization':
      return 1;
    case 'authority_governance':
      return 2;
    default:
      return 3;
  }
}

/** Descer de nível reduz garantia — é o gatilho da confirmação. */
export function lowers(current: SitePosture, next: SitePosture): boolean {
  return postureLevel(next) < postureLevel(current);
}

/** Só L3 exige o vínculo com o artefato. Espelha `domain.SitePosture`. */
export function requiresArtifactBinding(posture: SitePosture): boolean {
  return posture === 'artifact_binding';
}

export function postureLabel(posture: SitePosture): string {
  return t(`sitePosture.${posture}`);
}

export function postureHelp(posture: SitePosture): string {
  return t(`sitePosture.${posture}Help`);
}
